// The fonts a slide can use beyond its theme's (shared/slideDesign.ts FONTS), self-hosted from @fontsource (Latin only)
// and registered here as faces the browser loads only when a slide's words are drawn in them, as an @font-face rule
// is: a deck that uses none downloads none. Geist, Instrument Serif, JetBrains Mono and Caveat are the board's own and
// are in already (src/main.tsx).

import interN from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import interI from "@fontsource-variable/inter/files/inter-latin-wght-italic.woff2?url";
import dmSansN from "@fontsource-variable/dm-sans/files/dm-sans-latin-wght-normal.woff2?url";
import dmSansI from "@fontsource-variable/dm-sans/files/dm-sans-latin-wght-italic.woff2?url";
import manropeN from "@fontsource-variable/manrope/files/manrope-latin-wght-normal.woff2?url";
import jakartaN from "@fontsource-variable/plus-jakarta-sans/files/plus-jakarta-sans-latin-wght-normal.woff2?url";
import jakartaI from "@fontsource-variable/plus-jakarta-sans/files/plus-jakarta-sans-latin-wght-italic.woff2?url";
import plexN from "@fontsource-variable/ibm-plex-sans/files/ibm-plex-sans-latin-wght-normal.woff2?url";
import plexI from "@fontsource-variable/ibm-plex-sans/files/ibm-plex-sans-latin-wght-italic.woff2?url";
import montserratN from "@fontsource-variable/montserrat/files/montserrat-latin-wght-normal.woff2?url";
import montserratI from "@fontsource-variable/montserrat/files/montserrat-latin-wght-italic.woff2?url";
import poppins400 from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url";
import poppins400i from "@fontsource/poppins/files/poppins-latin-400-italic.woff2?url";
import poppins600 from "@fontsource/poppins/files/poppins-latin-600-normal.woff2?url";
import poppins700 from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";
import poppins700i from "@fontsource/poppins/files/poppins-latin-700-italic.woff2?url";
import outfitN from "@fontsource-variable/outfit/files/outfit-latin-wght-normal.woff2?url";
import groteskN from "@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2?url";
import playfairN from "@fontsource-variable/playfair-display/files/playfair-display-latin-wght-normal.woff2?url";
import playfairI from "@fontsource-variable/playfair-display/files/playfair-display-latin-wght-italic.woff2?url";
import frauncesN from "@fontsource-variable/fraunces/files/fraunces-latin-wght-normal.woff2?url";
import frauncesI from "@fontsource-variable/fraunces/files/fraunces-latin-wght-italic.woff2?url";
import loraN from "@fontsource-variable/lora/files/lora-latin-wght-normal.woff2?url";
import loraI from "@fontsource-variable/lora/files/lora-latin-wght-italic.woff2?url";
import sourceSerifN from "@fontsource-variable/source-serif-4/files/source-serif-4-latin-wght-normal.woff2?url";
import sourceSerifI from "@fontsource-variable/source-serif-4/files/source-serif-4-latin-wght-italic.woff2?url";
import dmSerifN from "@fontsource/dm-serif-display/files/dm-serif-display-latin-400-normal.woff2?url";
import dmSerifI from "@fontsource/dm-serif-display/files/dm-serif-display-latin-400-italic.woff2?url";
import bebasN from "@fontsource/bebas-neue/files/bebas-neue-latin-400-normal.woff2?url";
import syneN from "@fontsource-variable/syne/files/syne-latin-wght-normal.woff2?url";
import { FONT } from "../../../shared/slideDesign";

type Face = [url: string, weight: string, style?: "italic"];

/** Each hosted font's files: a variable font's one file covers its weights. */
const FILES: Record<string, Face[]> = {
  inter: [[interN, "100 900"], [interI, "100 900", "italic"]],
  "dm-sans": [[dmSansN, "100 1000"], [dmSansI, "100 1000", "italic"]],
  manrope: [[manropeN, "200 800"]],
  "plus-jakarta-sans": [[jakartaN, "200 800"], [jakartaI, "200 800", "italic"]],
  "ibm-plex-sans": [[plexN, "100 700"], [plexI, "100 700", "italic"]],
  montserrat: [[montserratN, "100 900"], [montserratI, "100 900", "italic"]],
  poppins: [[poppins400, "400"], [poppins400i, "400", "italic"], [poppins600, "600"], [poppins700, "700"], [poppins700i, "700", "italic"]],
  outfit: [[outfitN, "100 900"]],
  "space-grotesk": [[groteskN, "300 700"]],
  "playfair-display": [[playfairN, "400 900"], [playfairI, "400 900", "italic"]],
  fraunces: [[frauncesN, "100 900"], [frauncesI, "100 900", "italic"]],
  lora: [[loraN, "400 700"], [loraI, "400 700", "italic"]],
  "source-serif-4": [[sourceSerifN, "200 900"], [sourceSerifI, "200 900", "italic"]],
  "dm-serif-display": [[dmSerifN, "400"], [dmSerifI, "400", "italic"]],
  "bebas-neue": [[bebasN, "400"]],
  syne: [[syneN, "400 800"]],
};

let registered = false;

/**
 * The hosted fonts made known to the page, once: nothing downloads until words are drawn in one (a FontFace added to
 * the document loads when it is needed, as an @font-face rule does).
 */
export function registerSlideFonts() {
  if (registered || typeof document === "undefined" || typeof FontFace === "undefined") return;
  registered = true;
  for (const [id, faces] of Object.entries(FILES)) {
    const f = FONT[id];
    if (!f) continue;
    for (const [url, weight, style] of faces) {
      try {
        document.fonts.add(new FontFace(f.family, `url(${url}) format("woff2")`, { weight, style: style ?? "normal", display: "swap" }));
      } catch {
        /* a browser without FontFace draws the fallback */
      }
    }
  }
}

registerSlideFonts();

/** The hosted fonts made known to another window's page too (the presenter's own), so a theme's fonts draw there (S6). */
export function registerSlideFontsIn(doc: Document) {
  const Face = (doc.defaultView as (Window & { FontFace?: typeof FontFace }) | null)?.FontFace;
  if (!Face || !doc.fonts) return;
  for (const [id, faces] of Object.entries(FILES)) {
    const f = FONT[id];
    if (!f) continue;
    for (const [url, weight, style] of faces) {
      try {
        doc.fonts.add(new Face(f.family, `url(${new URL(url, location.href).href}) format("woff2")`, { weight, style: style ?? "normal", display: "swap" }));
      } catch {
        /* the fallback draws */
      }
    }
  }
}

/** Loads a font now (the font menu draws each name in its own), and says when it is in. */
export function loadFont(id: string): Promise<void> {
  const f = FONT[id];
  if (!f || typeof document === "undefined" || !document.fonts?.load) return Promise.resolve();
  return document.fonts.load(`400 32px '${f.family}'`).then(
    () => undefined,
    () => undefined,
  );
}
